import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Nav,
  Tab,
} from "react-bootstrap";
import { useFormik } from "formik";
import clsx from "clsx";
import styles from "./style/LectureFormModal.module.css";
import * as Yup from "yup";

import { getVideoDuration, convertTimeHMS } from "@helpers";
import { InputField, TextArea } from "@components/Input";
import { VideoUploader } from "@components/VideoUploader";
import { AttachmentUploader } from "@components/AttachmentUploader";

const LectureFormModal = ({
  initialValues,
  onSubmit,
  children,
  submitText,
}) => {
  const validateSchema = Yup.object().shape({
    title: Yup.string()
      .required("Lecture title is required")
      .max(100, "Lecture title require maximum of 100 characters"),
    description: Yup.string().required("Lecture description is required"),
    videoUrl: Yup.string().when("videoExternalUrl", {
      is: (val) => {
        let result = val == null || val == "";
        return result;
      },
      then: Yup.string().required("Video lecture is required"),
    }),
    videoExternalUrl: Yup.string()
      .nullable()
      .notRequired()
      .url("This field must be URL"),
  });

  const [tab, setTab] = useState("basic");
  const [videoTab, setVideoTab] = useState("upload");
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validateSchema,
    validateOnChange: true,
    onSubmit: (values) => {
      onSubmit(values);
      handleClose();
    },
  });

  // Handle Show/Hide Lecture Modal
  const [show, setShow] = useState(false);

  const handleClose = () => {
    setShow(false);
    setTab("basic");
    formik.resetForm();
  };
  const handleShow = () => {
    setShow(true);
  };

  // Handle Upload

  const handleUploadVideoLecture = ({ src, duration, name, type }) => {
    formik.setFieldValue("totalTime", duration);

    const { hours, minutes, seconds } = convertTimeHMS(duration);
    formik.setFieldValue("hours", hours);
    formik.setFieldValue("minutes", minutes);
    formik.setFieldValue("seconds", seconds);

    formik.setFieldValue("videoUrl", src);
    formik.validateField("videoUrl");
  };

  const handleUploadAttachment = (attachments) => {
    formik.setFieldValue("attachments", attachments);
  };

  const handleSelectTab = (key) => {
    setTab(key);
  };
  // Side Effect
  useEffect(() => {
    formik.setValues(initialValues);
    const { hours, minutes, seconds } = convertTimeHMS(initialValues.totalTime);
    formik.setFieldValue("hours", hours);
    formik.setFieldValue("minutes", minutes);
    formik.setFieldValue("seconds", seconds);
  }, [initialValues]);

  const handleSubmit = (event) => {
    formik.handleSubmit(event);
  };
  useEffect(() => {
    if (formik.errors.title || formik.errors.description) {
      setTab("basic");
    } else if (formik.values.title && formik.values.description) {
      if (formik.touched.videoUrl && formik.errors.videoUrl && tab != "video") {
        setTab("video");
        setVideoTab("upload");
        // formik.setErrors({});
      } else if (formik.errors.videoUrl && formik.touched.videoExternalUrl) {
        if (tab == "video") {
          setVideoTab("upload");
        } else {
          setTab("video");
          setVideoTab("upload");
        }
      }
    }
  }, [formik.submitCount]);

  return (
    <div>
      <div onClick={handleShow}>{children}</div>

      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Add Lecture</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={clsx(styles.tabContainer, "container")}>
            <form>
              <Tab.Container
                defaultActiveKey={"basic"}
                onSelect={handleSelectTab}
                activeKey={tab}
              >
                <Nav variant="pills" className="mt-3 mb-4 row m-0 p-0">
                  <Nav.Item className="col-12 col-lg-4 p-0">
                    <Nav.Link eventKey="basic">
                      <i className="bi bi-file-earmark-text-fill mr-2"></i>
                      Basic
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="col-12 col-lg-4 p-0">
                    <Nav.Link eventKey="video" disabled={formik.status}>
                      <i className="bi bi-camera-video-fill mr-2"></i>Video
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="col-12 col-lg-4 p-0">
                    <Nav.Link eventKey="attachments">
                      <i className="bi bi-paperclip mr-2"></i>Attachments
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey="basic">
                    <div className="mb-3">
                      <InputField
                        name={"title"}
                        type={"text"}
                        label={"Lecture Title*"}
                        placeholder="Lecture title here"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        autoComplete="off"
                        touchedValue={formik.touched.title}
                        errorsValue={formik.errors.title}
                      />
                    </div>

                    <div className="mb-3">
                      <TextArea
                        label="Description*"
                        type={"textarea"}
                        name="description"
                        placeholder={"Description here..."}
                        rows={5}
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        autoComplete="off"
                        touchedValue={formik.touched.description}
                        errorsValue={formik.errors.description}
                      />
                    </div>
                  </Tab.Pane>

                  <Tab.Pane eventKey="video">
                    <div className="container">
                      <h6>Upload your video lecture</h6>
                      <div
                        className={clsx(styles.tabChildContainer, "mb-4 mt-3")}
                      >
                        <Tab.Container
                          defaultActiveKey="upload"
                          activeKey={videoTab}
                          onSelect={(key) => setVideoTab(key)}
                        >
                          <Tab.Content>
                            <Tab.Pane eventKey="upload">
                              <div className="">
                                <VideoUploader
                                  error={
                                    formik.touched.videoUrl &&
                                    formik.errors.videoUrl
                                  }
                                  onUploadFinish={handleUploadVideoLecture}
                                  src={formik.values.videoUrl}
                                />

                                <h6 className="mt-4">
                                  Video Runtime - hh:mm:ss*
                                </h6>
                                <InputGroup className="mb-3 w-50">
                                  <FormControl
                                    placeholder="hh"
                                    type="number"
                                    name="hours"
                                    value={formik.values.hours}
                                    disabled
                                    max={60}
                                    maxLength={2}
                                  />
                                  <FormControl
                                    placeholder="mm"
                                    aria-label=""
                                    aria-describedby="basic-addon1"
                                    name="minutes"
                                    value={formik.values.minutes}
                                    disabled
                                  />
                                  <FormControl
                                    placeholder="ss"
                                    aria-label=""
                                    aria-describedby="basic-addon1"
                                    name="seconds"
                                    value={formik.values.seconds}
                                    disabled
                                  />
                                </InputGroup>
                              </div>
                            </Tab.Pane>
                          </Tab.Content>
                        </Tab.Container>
                      </div>
                    </div>
                  </Tab.Pane>

                  <Tab.Pane eventKey="attachments">
                    <AttachmentUploader
                      onUploadFinish={handleUploadAttachment}
                      src={formik.values.fileUrl}
                      initAttachments={formik.values.attachments}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </form>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button onClick={handleSubmit}>{submitText}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

/* required: display name */
LectureFormModal.displayName = "LectureFormModal";

/* required: props types */
LectureFormModal.propTypes = {
  initialValues: PropTypes.any,
  onSubmit: PropTypes.func,
  submitText: PropTypes.string,
};

/* required: default props */
LectureFormModal.defaultProps = {
  initialValues: {
    title: "",
    description: "",
    videoUrl: "",
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalTime: 0,
    attachments: [],
    videoExternalUrl: "",
    fileUrl: "",
  },
  onSubmit: () => {},
  submitText: "Add Lecture",
};

export { LectureFormModal };
